import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import { DEFAULT_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import { generateTimeSelectOptions } from '@/components/TimeSelect/TimeSelect'
import type { GA4PageNameType } from '@/ga4types'
import useBootstrapData from '@/hooks/useBootstrapData'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import type { DateRange, ITravelerInfo } from '@pcln/horizon'
import {
  Button,
  CheckboxWithLabel,
  DatePicker,
  Select,
  TRANSFORMER_MAX_MOBILE_WIDTH_PX,
  TravelerSelection,
  cn,
} from '@pcln/horizon'
import { addMonths } from 'date-fns/addMonths'
import { format } from 'date-fns/format'
import { parse } from 'date-fns/parse'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMediaQuery } from 'react-responsive'
import { handleSubmitForCars } from '../SearchForm/utils'
import searchFormSchema from '../SearchForm/validation'
import { fireBundleAndSaveEvent, fireItemClickEvent } from '../ga4'
import type { CAR_FORM_STATE_TYPE } from '../types'
import { getGridContainerClass, getInputClass } from './HorizonCarForm.variants'
import { LABELS } from './constants'
import useCarFormInitialValues from './useCarFormInitialValues'
import useCarFormSubmitValues from './useCarFormSubmitValues'

type FormType = {
  onSubmit?: (data: CAR_FORM_STATE_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  pageName?: 'homepage' | 'landing'
  useCompactLayout?: boolean
}

export default function HorizonCarForm({
  onSubmit = handleSubmitForCars,
  setLocation = () => null,
  pageName = 'homepage',
  useCompactLayout = false,
}: Readonly<FormType>) {
  const { locale: { localeCode = 'en-US' } = {} } = useBootstrapData()
  const { initialValues, isMobile, cguid } = useCarFormInitialValues()
  const { registerTabElement } = useTabGroup()
  const dropoffDatePickerTriggerRef = useRef<HTMLButtonElement | null>(null)
  const dropoffDatePickerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (dropoffDatePickerTimeoutRef.current) {
        clearTimeout(dropoffDatePickerTimeoutRef.current)
      }
    },
    [],
  )

  const isMobileComponent = useMediaQuery({ maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX })

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
    reset,
  } = useForm<CAR_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange',
  })

  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartTime,
    watchEndTime,
    watchOneWay,
    watchStartDate,
    watchEndDate,
    resetFormToInitialValues,
  } = useCarFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    reset,
    initialValues,
    setLocation,
  })

  const currentDate = new Date()
  const calendarMaxMonth = addMonths(currentDate, 11)

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    STAY: false,
    FLY: false,
  })

  const isHotel = bundleSelections.STAY
  const isFlight = bundleSelections.FLY

  const isPackage = watchTripType.includes('STAY') || watchTripType.includes('FLY')

  const [defaultRangeMonth, setDefaultRangeMonth] = useState(currentDate)
  const [defaultPickupMonth, setDefaultPickupMonth] = useState(currentDate)
  const [defaultDropoffMonth, setDefaultDropoffMonth] = useState(currentDate)

  const onCalendarChange = useCallback(
    (dates: DateRange): void => {
      if (dates?.from) {
        const startDate = format(dates.from, 'yyyyMMdd')
        setValue('hotelStartDate', startDate)
        setDefaultRangeMonth(dates?.from)
      }

      if (dates?.to) {
        const endDate = format(dates.to, 'yyyyMMdd')
        setValue('hotelEndDate', endDate)
      }
    },
    [setValue],
  )

  const onSingleDateChange = useCallback(
    (date: Date): void => {
      const formattedDate = format(date, 'yyyyMMdd')
      setValue('startDate', formattedDate, { shouldDirty: true })
      setDefaultPickupMonth(date)
      // Always update default dropoff month to match pickup date so the dropoff calendar opens to the same month
      setDefaultDropoffMonth(date)

      // Only show date range error when dropoff is already selected (not when initially selecting pickup)
      const hasDateRangeError = watchEndDate && formattedDate > watchEndDate
      if (hasDateRangeError) {
        void trigger('endDate')
      }

      if (!isMobile && !hasDateRangeError) {
        if (dropoffDatePickerTimeoutRef.current) {
          clearTimeout(dropoffDatePickerTimeoutRef.current)
        }
        dropoffDatePickerTimeoutRef.current = setTimeout(() => {
          dropoffDatePickerTimeoutRef.current = null
          dropoffDatePickerTriggerRef.current?.click()
        }, 0)
      } else if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [setValue, watchEndDate, isMobile, trigger],
  )

  const onEndDateChange = useCallback(
    (date: Date): void => {
      const formattedDate = format(date, 'yyyyMMdd')
      setValue('endDate', formattedDate, { shouldDirty: true })
      setDefaultDropoffMonth(date)
      if (watchStartDate && formattedDate === watchStartDate) {
        void trigger('endTime')
      }
    },
    [setValue, trigger, watchStartDate],
  )

  const onEndDateTimeChange = useCallback(
    (time: string): void => {
      setValue('endTime', time, { shouldDirty: true })
      void trigger('endTime')
    },
    [setValue, trigger],
  )

  const onStartTimeChange = useCallback(
    (time: string): void => {
      setValue('startTime', time)
      if (watchStartDate && watchEndDate && watchStartDate === watchEndDate) {
        void trigger('endTime')
      }
    },
    [setValue, trigger, watchStartDate, watchEndDate],
  )

  const onItemSelect = useCallback(
    (item: LOCATION_SEARCH_TYPE | null, searchKey: 'startLocation' | 'endLocation') => {
      setValue(searchKey, item)
      fireItemClickEvent()
      if (isMobile && !!item && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue],
  )

  const handleSubmitHelper = handleSubmit((data: CAR_FORM_STATE_TYPE) => {
    onSubmit(data, pageName)
    logSearchFormAnalytics(cguid, pageName, 'cars', isMobile)
  })

  const handleTravelerChange = (travelerInfo: ITravelerInfo): void => {
    setValue('travelers', travelerInfo as TravelerState)
  }

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections((prev) => ({
        ...prev,
        [optionValue]: checked,
      }))
      resetFormToInitialValues({
        tripType: updatedTripType,
        startLocation: watchStartLocation,
        endLocation: watchEndLocation,
      })
      if (checked) {
        try {
          fireBundleAndSaveEvent(updatedTripType)
        } catch {
          // GA4TripTypeMapper may throw on unexpected trip type combinations
        }
      }
    },
    [resetFormToInitialValues, watchStartLocation, watchEndLocation],
  )

  const handleDifferentDropOffChange = useCallback(() => {
    const newValue = !watchOneWay
    setValue('oneWay', newValue, { shouldDirty: true })
    resetFormToInitialValues({
      oneWay: newValue,
      startLocation: watchStartLocation,
      endLocation: watchEndLocation,
      startDate: watchStartDate,
      endDate: watchEndDate,
      startTime: watchStartTime,
      endTime: watchEndTime,
    })
  }, [
    setValue,
    resetFormToInitialValues,
    watchOneWay,
    watchEndDate,
    watchEndTime,
    watchStartDate,
    watchStartTime,
    watchStartLocation,
    watchEndLocation,
  ])

  return (
    <div className='w-auto'>
      <form className='w-auto' aria-label='horizon-car-search-form' onSubmit={handleSubmitHelper}>
        <div className='mx-auto w-auto'>
          <div className={getGridContainerClass(watchOneWay, isHotel, isFlight, useCompactLayout)}>
            {!isFlight ? (
              <>
                {/* Pickup Location Input, For STAY package. */}
                <div className={getInputClass('pickup', watchOneWay, isHotel, isFlight, useCompactLayout)}>
                  <AutocompleteWrapper
                    key={`pickup-${watchTripType}`}
                    errorMessage={isHotel ? errors.endLocation?.message : errors.startLocation?.message}
                    searchKey={isHotel ? 'endLocation' : 'startLocation'}
                    label={LABELS.DEFAULT_LOCATION}
                    typeAheadEndpoint='cars'
                    initialSelectedItem={watchStartLocation as LOCATION_SEARCH_TYPE | undefined}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(itemSelected, 'startLocation')
                      if (isHotel) {
                        onItemSelect(itemSelected, 'endLocation')
                      }
                    }}
                  />
                </div>
                {/* Dropoff Location Input */}
                {watchOneWay && !isPackage && (
                  <div className={getInputClass('dropoff', watchOneWay, isHotel, isFlight, useCompactLayout)}>
                    <AutocompleteWrapper
                      key={`dropoff-${watchTripType}`}
                      errorMessage={errors.endLocation?.message}
                      searchKey='endLocation'
                      label={LABELS.DEFAULT_LOCATION}
                      typeAheadEndpoint='cars'
                      initialSelectedItem={watchEndLocation as LOCATION_SEARCH_TYPE | undefined}
                      onItemSelect={(itemSelected) => {
                        onItemSelect(itemSelected, 'endLocation')
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={getInputClass('flightInput')}>
                  <AutocompleteWrapper
                    key={`flight-origin-${watchTripType}`}
                    errorMessage={errors.startLocation?.message}
                    searchKey='startLocation'
                    label='Where from?'
                    typeAheadEndpoint='flights'
                    initialSelectedItem={watchStartLocation as LOCATION_SEARCH_TYPE | undefined}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(itemSelected, 'startLocation')
                    }}
                  />
                </div>
                <div className={getInputClass('flightInput')}>
                  <AutocompleteWrapper
                    key={`flight-destination-${watchTripType}`}
                    errorMessage={errors.endLocation?.message}
                    searchKey='endLocation'
                    label='Where to?'
                    typeAheadEndpoint='flights'
                    initialSelectedItem={watchEndLocation as LOCATION_SEARCH_TYPE | undefined}
                    onItemSelect={(itemSelected) => {
                      onItemSelect(itemSelected, 'endLocation')
                    }}
                  />
                </div>
              </>
            )}

            {/* Dropoff type checkbox - placed after autocomplete inputs */}
            <div className={getInputClass('options', watchOneWay, isHotel, isFlight, useCompactLayout)}>
              <CheckboxWithLabel
                size='16'
                name='differentDropOff'
                id='differentDropOff'
                value='differentDropOff'
                checked={watchOneWay}
                handleChange={handleDifferentDropOffChange}
                disabled={isPackage}
                labelNode={<span>{LABELS.DIFFERENT_DROPOFF}</span>}
                labelClassName='ml-2'
              />
            </div>

            {!isPackage ? (
              <>
                {/* Pickup date */}
                <div className={getInputClass('datePickerWrapper')}>
                  <DatePicker
                    key={`pickup-date-${watchTripType}`}
                    errorMessage={errors.startDate?.message}
                    label={LABELS.PICKUP_DATE}
                    mode='single'
                    numberOfMonths={isMobileComponent ? 12 : 1}
                    handleCalendarChange={onSingleDateChange}
                    disabled={{
                      before: currentDate,
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultPickupMonth}
                    inputRef={registerTabElement}
                    collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                  />
                </div>
                {/* Pickup time */}
                <Select
                  label={LABELS.PICKUP_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  menuWidth='150px'
                  initialValue={watchStartTime}
                  onChange={onStartTimeChange}
                  placeholder={LABELS.PICKUP_TIME}
                />
                {/* Dropoff date */}
                <div className={getInputClass('datePickerWrapper')}>
                  <DatePicker
                    key={`dropoff-date-${watchTripType}-${watchStartDate}`}
                    errorMessage={errors.endDate?.message}
                    label={LABELS.DROPOFF_DATE}
                    mode='single'
                    numberOfMonths={isMobileComponent ? 12 : 1}
                    handleCalendarChange={onEndDateChange}
                    disabled={{
                      before: watchStartDate ? parse(watchStartDate, 'yyyyMMdd', currentDate) : currentDate,
                    }}
                    startMonth={watchStartDate ? parse(watchStartDate, 'yyyyMMdd', currentDate) : currentDate}
                    endMonth={calendarMaxMonth}
                    defaultMonth={defaultDropoffMonth}
                    inputRef={(el) => {
                      dropoffDatePickerTriggerRef.current = el
                      registerTabElement(el)
                    }}
                    collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                  />
                </div>
                {/* Dropoff time */}
                <Select
                  label={LABELS.DROPOFF_TIME}
                  isInset
                  menuItems={generateTimeSelectOptions(localeCode, true)}
                  menuWidth='150px'
                  initialValue={watchEndTime}
                  onChange={onEndDateTimeChange}
                  placeholder={LABELS.DROPOFF_TIME}
                  isError={!!errors.endTime?.message}
                  errorTooltipText={errors.endTime?.message}
                />
              </>
            ) : (
              <>
                <div className={getInputClass('packageDatePickerWrapper')}>
                  <DatePicker
                    key={`package-dates-${watchTripType}`}
                    errorMessage={errors.hotelEndDate?.message || errors.hotelStartDate?.message}
                    className='flight-dates'
                    title='Departing – Returning'
                    label='Departing – Returning'
                    mode='range'
                    numberOfMonths={isMobileComponent ? 12 : 2}
                    handleCalendarChange={onCalendarChange}
                    defaultMonth={defaultRangeMonth}
                    disabled={{
                      before: currentDate,
                    }}
                    startMonth={currentDate}
                    endMonth={calendarMaxMonth}
                    min={isHotel ? 1 : 0}
                    collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                  />
                </div>
                <div className={getInputClass('packageDatePickerWrapper')}>
                  <TravelerSelection
                    isHotel={isHotel && !isFlight}
                    initialValues={initialValues.travelers}
                    roomsEnabled={isHotel}
                    maxRooms={4}
                    maxChildren={6}
                    maxTravelers={8}
                    ageSelectEnabled
                    tooltipErrorMessageId='travelerSelectionError'
                    errorMessage={
                      errors?.travelers?.childrenAges?.message ||
                      errors?.travelers?.children?.message ||
                      errors?.travelers?.rooms?.message ||
                      ''
                    }
                    onChange={(values: ITravelerInfo) => {
                      handleTravelerChange(values)
                    }}
                    collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
                    disclaimer='You may only book a package for up to 8 travelers.'
                  />
                </div>
              </>
            )}

            {/* Bundle and Save */}
            <div className={cn(getInputClass('package', watchOneWay, isHotel, isFlight, useCompactLayout))}>
              <HorizonBundleAndSave
                productType='cars'
                checkedValues={bundleSelections}
                onSelectionChange={handleBundleSelectionChange}
                tripType={watchTripType}
              />
            </div>

            {/* Search Button */}
            <Button
              type='primary'
              size='lg'
              buttonType='submit'
              className={cn(
                getInputClass('submit', watchOneWay, isHotel, isFlight, useCompactLayout),
                isPackage && 'sm:col-span-1',
                isMobile && 'fixed right-4 bottom-4 left-4',
              )}
            >
              {LABELS.SEARCH_BUTTON}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
